import type { CartItem } from '@cobrastyle/shared-types';

/**
 * Keep a cart line's chosen options visible across a quantity change.
 *
 * The adapter's `updateCartItems` / `removeItemFromCart` queries do not request
 * the `ConfigurableCartItem` / `BundleCartItem` fragments the way its `cart`
 * query does, so the cart those mutations return has `configurableOptions` and
 * `bundleOptions` undefined on every line. The client sets that as state, the
 * rows re-render without their options, and they only reappear once the full
 * cart is refetched — a visible blink, measured at roughly half a second on a
 * bundle line.
 *
 * The real fix belongs in @cobrastyle/adapter-magento2, which is a published
 * package this repo consumes as compiled dist. Until that ships, remembering
 * what a line already told us is enough, and it costs no extra round trip.
 *
 * Carrying options forward by `uid` is safe because Magento's cart item uid
 * identifies the CONFIGURATION, not the product: choosing different options
 * produces a different line with a different uid. So a uid that is still in the
 * cart still has exactly the options it had a moment ago.
 */
export type LineOptions = Pick<CartItem, 'configurableOptions' | 'bundleOptions'>;

function hasOptions(item: LineOptions): boolean {
  return (
    (item.configurableOptions?.length ?? 0) > 0 || (item.bundleOptions?.length ?? 0) > 0
  );
}

/**
 * Record the options of any line that has them, and restore them onto lines
 * that arrived without. Mutates `memo` — it is a cache belonging to the caller,
 * held across renders.
 */
export function preserveLineOptions(items: CartItem[], memo: Map<string, LineOptions>): CartItem[] {
  for (const item of items) {
    if (hasOptions(item)) {
      memo.set(item.uid, {
        configurableOptions: item.configurableOptions,
        bundleOptions: item.bundleOptions,
      });
    }
  }

  // Drop lines that have left the cart, so the map cannot grow without bound
  // across a long session of edits.
  const live = new Set(items.map((item) => item.uid));
  for (const uid of memo.keys()) {
    if (!live.has(uid)) memo.delete(uid);
  }

  return items.map((item) => {
    if (hasOptions(item)) return item;
    const remembered = memo.get(item.uid);
    return remembered ? { ...item, ...remembered } : item;
  });
}
